import React from "react";
import { OfferStatus } from "../../types";
import { LinkIcon, CheckIcon } from "../icons";

interface PublishedOfferBannerProps {
  status: OfferStatus;
  publicToken: string;
  publishedAt?: Date;
  mainOfferPublicToken?: string;
}

const PublishedOfferBanner: React.FC<PublishedOfferBannerProps> = ({
  status,
  publicToken,
  publishedAt,
  mainOfferPublicToken,
}) => {
  const getStatusConfig = (status: OfferStatus) => {
    switch (status) {
      case OfferStatus.PUBLISHED:
        return {
          bgColor: "bg-blue-50",
          borderColor: "border-blue-200",
          iconColor: "text-blue-600",
          textColor: "text-blue-800",
          icon: <LinkIcon className="h-5 w-5" />,
          title: "Oferta opublikowana",
          description:
            "Ta oferta została opublikowana i jest dostępna pod publicznym linkiem.",
        };
      case OfferStatus.ACCEPTED:
        return {
          bgColor: "bg-green-50",
          borderColor: "border-green-200",
          iconColor: "text-green-600",
          textColor: "text-green-800",
          icon: <CheckIcon className="h-5 w-5" />,
          title: "Oferta zaakceptowana",
          description: "Ta oferta została zaakceptowana przez klienta.",
        };
      case OfferStatus.REJECTED:
        return {
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
          iconColor: "text-red-600",
          textColor: "text-red-800",
          icon: (
            <span className="h-5 w-5 flex items-center justify-center font-bold">
              ✕
            </span>
          ),
          title: "Oferta odrzucona",
          description: "Ta oferta została odrzucona przez klienta.",
        };
      default:
        return null;
    }
  };

  if (status === OfferStatus.DRAFT) {
    return null;
  }

  const config = getStatusConfig(status);
  if (!config) return null;

  // Use mainOfferPublicToken if available (for versions), otherwise use publicToken
  const tokenToUse = mainOfferPublicToken || publicToken;
  const publicUrl = `${window.location.origin}/offers/public/${tokenToUse}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(publicUrl);
    // TODO: Add toast notification
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("pl-PL", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  return (
    <div
      className={`${config.bgColor} ${config.borderColor} border rounded-lg p-6 mb-6`}
    >
      <div className="flex items-start space-x-4">
        <div className={`${config.iconColor} mt-1 flex-shrink-0`}>
          {config.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-lg font-semibold ${config.textColor}`}>
                {config.title}
              </h3>
              <p className={`mt-1 ${config.textColor} opacity-90`}>
                {config.description}
              </p>
              {publishedAt && (
                <p className={`mt-2 text-sm ${config.textColor} opacity-75`}>
                  Opublikowana: {formatDate(publishedAt)}
                </p>
              )}
            </div>
            {(status === OfferStatus.PUBLISHED ||
              status === OfferStatus.ACCEPTED) && (
              <div className="flex-shrink-0 ml-4">
                <div className="flex space-x-2">
                  <button
                    onClick={() => window.open(publicUrl, "_blank")}
                    className={`px-4 py-2 text-sm font-medium ${config.textColor} bg-white border ${config.borderColor} rounded-md hover:bg-gray-50 transition-colors`}
                  >
                    Zobacz publiczną wersję
                  </button>
                  <button
                    onClick={copyToClipboard}
                    className={`px-3 py-2 text-sm font-medium ${config.textColor} bg-white border ${config.borderColor} rounded-md hover:bg-gray-50 transition-colors`}
                    title="Skopiuj link"
                  >
                    📋
                  </button>
                </div>
              </div>
            )}
          </div>

          {status === OfferStatus.PUBLISHED && (
            <div className="mt-4 bg-white border border-blue-200 rounded-md p-3">
              <label className="block text-sm font-medium text-blue-800 mb-2">
                Link publiczny:
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={publicUrl}
                  readOnly
                  className="flex-1 px-3 py-1 text-sm border border-blue-200 rounded bg-blue-50 text-blue-800 font-mono"
                />
                <button
                  onClick={copyToClipboard}
                  className="px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 rounded transition-colors"
                >
                  Kopiuj
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        className={`mt-4 p-3 bg-white border ${config.borderColor} rounded-md`}
      >
        <p className={`text-sm ${config.textColor} font-medium mb-1`}>
          ⚠️ Uwaga: Oferta jest zamknięta do edycji
        </p>
        <p className={`text-sm ${config.textColor} opacity-75`}>
          Nie można modyfikować treści opublikowanej oferty. W razie potrzeby
          możesz utworzyć nową ofertę na podstawie tej.
        </p>
      </div>
    </div>
  );
};

export default PublishedOfferBanner;
